import React, { useState, useEffect } from 'react';
import { Case } from '../types';
import { documentsApi, PendingDocument } from '../services/documents';
import { formatLocalDate } from '../utils/date';

export interface PendingAssignmentPanelProps {
  cases: Case[];
  onAssign: (documentId: string, caseId: string) => Promise<void>;
  onCreateCase: (documentId: string) => void;
  onRefresh?: () => void;
}

/**
 * Panel for displaying and managing documents pending case assignment.
 * Shows badge count, list view with document details, and quick-assign dropdown.
 * 
 * Requirements: 2.3.1.1, 2.3.1.2, 2.3.1.3, 2.3.1.4, 2.3.1.5
 */
export const PendingAssignmentPanel: React.FC<PendingAssignmentPanelProps> = ({
  cases,
  onAssign,
  onCreateCase,
  onRefresh,
}) => {
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  const loadPendingDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await documentsApi.getPendingDocuments();
      setDocuments(result.documents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingDocuments();
  }, []);

  const handleAssign = async (documentId: string, caseId: string) => {
    if (!caseId) return;
    
    try {
      setAssigningId(documentId);
      await onAssign(documentId, caseId);
      // Remove from local list
      setDocuments(prev => prev.filter(d => d.documentId !== documentId));
      onRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign document');
    } finally {
      setAssigningId(null);
    }
  };

  const getSourceIcon = (source: 'upload' | 'email'): string => {
    return source === 'email' ? '📧' : '📤';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSuggestedCase = (doc: PendingDocument): Case | undefined => {
    if (!doc.suggestedCaseId) return undefined;
    return cases.find(c => c.caseId === doc.suggestedCaseId);
  };

  const pendingCount = documents.length;

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}
      data-testid="pending-assignment-panel"
    >
      {/* Header with badge */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
        }}
        data-testid="panel-header"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600 }}>Pending Assignment</span>
          {pendingCount > 0 && (
            <span
              style={{
                backgroundColor: '#ff9800',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
              }}
              data-testid="pending-badge"
            >
              {pendingCount}
            </span>
          )}
        </div>
        <span style={{ color: '#666', fontSize: '18px' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </div>

      {/* Content */}
      {expanded && (
        <div style={{ padding: '16px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
              Loading...
            </div>
          )}

          {error && (
            <div
              style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
              data-testid="error-message"
            >
              {error}
              <button
                onClick={loadPendingDocuments}
                style={{
                  marginLeft: '12px',
                  background: 'none',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && documents.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '24px',
                color: '#666',
                backgroundColor: '#fafafa',
                borderRadius: '4px',
              }}
              data-testid="empty-state"
            >
              No documents pending assignment
            </div>
          )}

          {!loading && documents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {documents.map(doc => {
                const suggestedCase = getSuggestedCase(doc);
                const isAssigning = assigningId === doc.documentId;

                return (
                  <div
                    key={doc.documentId}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '12px',
                      backgroundColor: '#fafafa',
                    }}
                    data-testid={`pending-doc-${doc.documentId}`}
                  >
                    {/* Document info */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '24px' }}>{getSourceIcon(doc.source)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={doc.fileName}
                        >
                          {doc.fileName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {formatFileSize(doc.fileSize)} • {formatLocalDate(doc.uploadedAt)} • via {doc.source}
                        </div>
                        {suggestedCase && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#1976d2',
                              marginTop: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>💡</span>
                            <span>
                              Suggested: {suggestedCase.childLastName}, {suggestedCase.childFirstName}
                              {doc.suggestedCaseConfidence && (
                                <span style={{ color: '#666' }}>
                                  {' '}({Math.round(doc.suggestedCaseConfidence * 100)}% confidence)
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        onChange={(e) => handleAssign(doc.documentId, e.target.value)}
                        disabled={isAssigning}
                        defaultValue=""
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                        }}
                        data-testid={`assign-select-${doc.documentId}`}
                      >
                        <option value="" disabled>
                          {isAssigning ? 'Assigning...' : 'Assign to case...'}
                        </option>
                        {suggestedCase && (
                          <option value={suggestedCase.caseId}>
                            ⭐ {suggestedCase.childLastName}, {suggestedCase.childFirstName} (suggested)
                          </option>
                        )}
                        {cases
                          .filter(c => c.caseId !== suggestedCase?.caseId)
                          .map(c => (
                            <option key={c.caseId} value={c.caseId}>
                              {c.childLastName}, {c.childFirstName}
                            </option>
                          ))}
                      </select>
                      <button
                        onClick={() => onCreateCase(doc.documentId)}
                        disabled={isAssigning}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #4caf50',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          color: '#4caf50',
                          cursor: isAssigning ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        data-testid={`create-case-${doc.documentId}`}
                      >
                        + New Case
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Refresh button */}
          {!loading && (
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <button
                onClick={loadPendingDocuments}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                data-testid="refresh-button"
              >
                ↻ Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Badge component for showing pending document count in navigation/sidebar.
 */
export const PendingBadge: React.FC<{ count: number }> = ({ count }) => {
  if (count === 0) return null;

  return (
    <span
      style={{
        backgroundColor: '#ff9800',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 600,
        marginLeft: '8px',
      }}
      data-testid="pending-nav-badge"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

export default PendingAssignmentPanel;
